/**
 * Checks whether a DOM removal error is expected (e.g. removing a node that was already detached).
 * Useful to silence noisy cleanup errors that occur during React unmount flows.
 */
export const isExpectedDomRemovalError = (error: unknown): boolean => {
  if (typeof DOMException !== "undefined" && error instanceof DOMException) {
    if (error.name === "NotFoundError") {
      return true;
    }

    if (typeof error.code === "number" && error.code === 8) {
      // Legacy NOT_FOUND_ERR code used by some older engines
      return true;
    }
  }

  if (error instanceof Error) {
    const message = error.message;
    return message.includes("Failed to execute 'removeChild' on 'Node'") ||
      message.includes("The node to be removed is not a child of this node");
  }

  return false;
};

/** Logs unexpected DOM removal errors using a consistent format. */
export const logDomRemovalError = (context: string, error: unknown): void => {
  if (isExpectedDomRemovalError(error)) {
    return;
  }

  try {
    if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
      console.warn(`[DOM Cleanup] ${context}`, error);
    }
  } catch {
    // Ignore logging issues (e.g., import.meta not available)
  }
};

export const shouldIgnoreError = (error: unknown): boolean => {
  if (isExpectedDomRemovalError(error)) {
    return true;
  }

  if (error instanceof Error) {
    const message = error.message?.toLowerCase() || "";

    if (message.includes("network error") || message.includes("failed to fetch")) {
      return false;
    }

    if (message.includes("aborted") || message.includes("cancelled")) {
      return true;
    }

    if (message.includes("resizeobserver")) {
      return true;
    }
  }

  return false;
};

export const getErrorInfo = (error: unknown): { message: string; stack?: string; name?: string } => {
  if (error instanceof Error) {
    return {
      message: error.message,
      stack: error.stack,
      name: error.name,
    };
  }

  if (typeof error === "string") {
    return { message: error };
  }

  return { message: "Unknown error" };
};
